import axios from 'axios';
import { get, omit, pick } from 'lodash';
import { Config } from 'src/latest/src/constants/AppServerConfig';
import { StorageKey } from 'src/latest/src/constants/StorageKey';
import Storage from 'src/latest/src/utils/Storage';

/**
 * Single owner of the access token.
 *
 * Before this module the token was read straight out of localStorage in 14
 * places, and the refresh call was implemented three separate times
 * (twice in makeApiCall.js, once as getRefreshToken() in legacy/utils/helpers).
 * Those three had already drifted apart, and none of them shared a
 * concurrency guard that actually worked.
 *
 * Deliberately a leaf module: it imports only Config, Storage and axios, so
 * nothing here can form an import cycle with makeApiCall or the redux store.
 *
 * PHASE 1 of the refresh-token removal (see docs/refresh-token-removal-plan.md).
 * Storage is still the source of truth on first load; Phase 4 makes the token
 * memory-only and drops the fallbacks marked below.
 *
 * PHASE 2 split the credential from session identity. The blob under
 * StorageKey.accessToken used to hold the whole /token response merged with
 * the user's profile; it now holds only PERSISTED_FIELDS, and only this module
 * writes it. Identity (role, name, avatar) lives in the redux authentication
 * slice.
 */

export type TokenResponse = {
  access_token: string;
  expires_in?: number;
  token_type?: string;
  // eslint-disable-next-line camelcase
  refresh_token?: string;
  [key: string]: any;
};

/**
 * Phase 3/4 switch, off unless REACT_APP_MEMORY_ONLY_TOKEN is "true".
 *
 * With it on, startup re-establishes the session by refreshing instead of
 * reading storage, and `isAuthed` stops being seeded from localStorage. Phase 4
 * adds the rest: the token stops being persisted at all, and the refresh
 * credential moves to the HttpOnly cookie.
 */
export const isMemoryOnlyToken =
  process.env.REACT_APP_MEMORY_ONLY_TOKEN === 'true';

/** Fields that grant access. Nothing outside this module should keep them. */
export const TOKEN_FIELDS = ['access_token', 'refresh_token'];

/**
 * What persists under StorageKey.accessToken while the flag is off.
 *
 * `id` is not a credential; it is there because the pre-Phase-3 startup path
 * uses its presence to tell a full login apart from a magic-link session. With
 * the flag on nothing is persisted at all, so this list is dead — Phase 7
 * deletes it along with the storage-backed path.
 */
const PERSISTED_FIELDS = [...TOKEN_FIELDS, 'expires_in', 'id'];

/**
 * Keys that earlier builds filled with session material. Written as literals
 * because two of them no longer exist in StorageKey, while a returning user
 * still has all five sitting on disk.
 */
const LEGACY_SESSION_KEYS = [
  'i2i_accessToken',
  'i2i_accessToken_client',
  't2fa',
  'isAuthed',
  'loggedInUser',
];

let accessToken: string | null = null;
let refreshInFlight: Promise<TokenResponse> | null = null;

const readStoredToken = (): any => {
  // PHASE 4: nothing is persisted any more, so there is nothing to read.
  if (isMemoryOnlyToken) {
    return {};
  }

  try {
    return JSON.parse(Storage.getItem(StorageKey.accessToken) || '{}');
  } catch {
    return {};
  }
};

const writeStoredCredential = (credential: Partial<TokenResponse>): void => {
  accessToken = credential.access_token || null;

  // PHASE 4: the token lives in the variable above and nowhere else. The only
  // thing written is the marker that a session exists, so public pages know
  // whether a refresh is worth making on load.
  if (isMemoryOnlyToken) {
    if (accessToken) {
      Storage.setItem(StorageKey.hadSession, 'true');
    }

    return;
  }

  Storage.setItem(StorageKey.accessToken, JSON.stringify(credential));
};

/**
 * Strip the credential out of anything headed for identity state. Login flows
 * pass whole /token responses to authUser(), and the reducer persists what it
 * is given, so without this the tokens land in the `loggedInUser` key too.
 * @param {Object} details identity data that may carry token fields
 * @returns {Object} a copy without them
 */
export const withoutTokens = <T extends object>(details: T) =>
  omit(details, TOKEN_FIELDS);

/**
 * Persist a new session's credential, replacing whatever was stored before.
 * Anything outside PERSISTED_FIELDS — profile data, role, avatar — is dropped
 * here; that belongs to the redux authentication slice.
 * @param {Object} details a /token (or equivalent) response
 */
export const saveCredential = (details: any): void => {
  writeStoredCredential(pick(details, PERSISTED_FIELDS));

  // `t2fa` was a second plaintext copy of the access token, outside the blob
  // and the Storage wrapper. Nothing reads it any more; removing it here clears
  // it for returning users on their next login or reload.
  localStorage.removeItem('t2fa');
};

/**
 * Delete everything an earlier build persisted about the session.
 *
 * Runs once at startup under the flag. Existing users are carrying a token, a
 * profile and an `isAuthed` flag from before the switch, and none of it is read
 * any more — but it is still readable by anything that gets script into the
 * page, which is the whole point of the ticket.
 * @returns {void}
 */
export const purgeSessionStorage = (): void => {
  try {
    LEGACY_SESSION_KEYS.forEach(key => localStorage.removeItem(key));
  } catch {
    // storage unavailable (private mode, blocked cookies) — nothing to purge
  }
};

/**
 * The token to put on outgoing requests. Prefers the in-memory copy, which is
 * always the freshest — a refresh updates it before any caller sees the result.
 * @returns {String} the bearer token, or an empty string when there is none
 */
export const getAccessToken = (): string => {
  if (accessToken) {
    return accessToken;
  }

  // With the flag off this falls back to what login persisted. With it on,
  // readStoredToken() is empty by construction: a call made before startup has
  // a token goes out without a bearer and is retried once it does.
  return get(readStoredToken(), 'access_token', '') || '';
};

export const setAccessToken = (token: string): void => {
  accessToken = token;
};

/**
 * End the session as far as this tab is concerned: drop the in-memory token and
 * the marker saying there is a session to restore.
 * @returns {void}
 */
export const clearAccessToken = (): void => {
  accessToken = null;
  refreshInFlight = null;
  Storage.removeItem(StorageKey.hadSession);
};

/**
 * Whether this browser has held a session that a refresh might restore. Public
 * pages check this before refreshing, so a visitor who never signed in costs no
 * auth call at all.
 * @returns {Boolean} true once a memory-only session has been established here
 */
export const hasSessionHint = (): boolean =>
  Storage.getItem(StorageKey.hadSession) === 'true';

/**
 * Exchange the refresh credential for a new access token.
 *
 * Uses bare axios rather than the configured `api` instance on purpose: the
 * instance carries the 401 interceptor, and routing the refresh call through
 * it would let a failed refresh re-enter the interceptor and recurse.
 */
async function postRefresh(): Promise<TokenResponse> {
  const body = new URLSearchParams({ grant_type: 'refresh_token' });

  // With the flag off the Auth Server still expects the refresh token in the
  // request body, read from storage. With it on the credential is an HttpOnly
  // cookie the page cannot read: the body carries nothing but the grant type,
  // and the request has to be credentialed for the browser to attach it.
  //
  // On localhost the API is a different origin (see AppServerConfig:3-9), so a
  // credentialed request needs either the CRA dev proxy or an explicit
  // Access-Control-Allow-Origin echo with Allow-Credentials. That is the
  // localhost decision in Phase 0.
  if (!isMemoryOnlyToken) {
    const storedRefreshToken = get(readStoredToken(), 'refresh_token', '');

    if (storedRefreshToken) {
      body.append('refresh_token', storedRefreshToken);
    }
  }

  const { data } = await axios.post(
    `${Config.apiUrl}/${Config.tokenService}token`,
    body.toString(),
    {
      withCredentials: isMemoryOnlyToken,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${Config.clientCredentialInternal}`,
      },
    },
  );

  return data;
}

/**
 * Single-flight refresh. Concurrent 401s — a dashboard mounting eight requests
 * at once, say — all await the same in-flight call instead of each starting
 * their own, and all resume once it resolves.
 *
 * Persists the new credential itself, so every caller gets the same result
 * whether or not it knows about storage.
 *
 * Rejects if the refresh fails; callers decide what that means for the user.
 * @returns {Promise<TokenResponse>} the shared in-flight refresh
 */
export function refreshAccessToken(): Promise<TokenResponse> {
  if (!refreshInFlight) {
    refreshInFlight = postRefresh()
      .then(response => {
        // Success is `access_token` alone. The previous implementations also
        // required `refresh_token`, which turns a perfectly good refresh into
        // a forced logout the moment the BE stops returning that field.
        if (!response?.access_token) {
          throw new Error('Refresh response contained no access_token');
        }

        // Merge rather than replace: until Phase 4 the next refresh still
        // posts the stored refresh_token, and a response isn't guaranteed to
        // carry a new one. Picking from the stored side as well drops any
        // profile fields a pre-Phase-2 blob still holds.
        writeStoredCredential({
          ...pick(readStoredToken(), PERSISTED_FIELDS),
          ...pick(response, PERSISTED_FIELDS),
        });

        return response;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}
